import React from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { FiCheck, FiArrowLeft, FiArrowRight, FiBriefcase, FiUserPlus, FiAtSign, FiSend } from "react-icons/fi";
import { fetchWithAuth, errorText } from "../utils/authFetch";
import { creditsChanged, formatCredits, useCredits } from "../utils/useCredits";
import { useAuth } from "../context/AuthContext";
import { PLANS, usd, annualTotal, TOPUP_BLOCK_CREDITS, TOPUP_BLOCK_USD, ADDON_SETUP_USD, ADDON_MONTHLY_USD } from "./PricingPage";
import { DemoModal } from "./LandingPage";

/* After the plans page: an add-ons page, then a payment page, each with the order in a
   side panel. Airwallex's hosted page is only reached from the payment page; `?done=1`
   is the way back. `?topup=1` is the one-page extra-credits purchase. */

const DONE_POLL_MS = 2000;
const DONE_POLL_LIMIT = 45;

// An https link on airwallex.com or one of its subdomains (hosted checkout, live or demo).
function isAirwallexUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && (u.hostname === "airwallex.com" || u.hostname.endsWith(".airwallex.com"));
  } catch {
    return false;
  }
}

/* MainContent already pads the pane. */
const Page = styled.main`
  padding: 0 0 24px;
  width: 100%;
`;

const Head = styled.header`
  margin-bottom: 26px;

  h1 {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 52px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -0.015em;
    margin: 0;
    color: ${({ theme }) => theme.ink};
  }
  h1 em {
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
  p {
    margin: 16px 0 0;
    font-size: 16px;
    color: ${({ theme }) => theme.inkSoft};
  }

  @media (max-width: 760px) {
    h1 {
      font-size: 38px;
    }
    p {
      font-size: 15px;
      margin-top: 10px;
    }
  }
`;

const Crumb = styled.button`
  border: 0;
  background: none;
  padding: 0;
  margin-bottom: 14px;
  font: inherit;
  font-size: 13.5px;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 32px;
  align-items: start;

  @media (max-width: 1280px) {
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 20px;
  }

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 16px;
  padding: 24px 26px 26px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }
  > p {
    margin: 6px 0 0;
    font-size: 13.5px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkMuted};
  }
`;

const Summary = styled(Card)`
  position: sticky;
  top: 24px;
  padding: 30px 28px 30px;
  border-radius: 20px;

  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
  }
  h2 {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 30px;
    font-weight: 400;
    letter-spacing: -0.01em;
  }
  .change {
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    font-size: 15px;
    color: ${({ theme }) => theme.accent};
    cursor: pointer;
  }
  .change:hover {
    text-decoration: underline;
  }
  dl {
    margin: 22px 0 0;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px 16px;
    font-size: 15px;
  }
  dt {
    color: ${({ theme }) => theme.inkSoft};
  }
  dd {
    margin: 0;
    text-align: right;
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
    font-variant-numeric: tabular-nums;
  }

  .summary {
    margin: 10px 0 0;
    font-size: 15px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkSoft};
  }
  .summary b {
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }

  /* "This month" and "Every month after": each a labelled block of money lines, a total,
     and the credits that land in that period. */
  .group {
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid ${({ theme }) => theme.line};
  }
  /* What is paid now is the loud block: a tinted panel with the blue heading and the big
     total. What recurs sits below it, quieter. */
  .group.now {
    margin-top: 18px;
    padding: 18px 18px 16px;
    border-top: 0;
    border-radius: 14px;
    background: ${({ theme }) => theme.accentSoft};
  }
  .group.now h3 {
    color: ${({ theme }) => theme.accent};
  }
  .group.now .line,
  .group.now .sum {
    color: ${({ theme }) => theme.ink2};
  }
  .group.now .line small {
    color: ${({ theme }) => theme.inkSoft};
  }
  .group.now .sum {
    border-top-color: color-mix(in srgb, ${({ theme }) => theme.accent} 22%, transparent);
  }
  .group.now .credits {
    background: ${({ theme }) => theme.surface};
  }
  .group.later h3 {
    color: ${({ theme }) => theme.inkMuted};
  }
  .group.later .line b {
    font-weight: 500;
    color: ${({ theme }) => theme.inkSoft};
  }
  .group.later .sum strong {
    font-size: 24px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .group.later .credits b {
    font-size: 20px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .credits {
    margin-top: 12px;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${({ theme }) => theme.bg2};
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    font-size: 14.5px;
    color: ${({ theme }) => theme.inkSoft};
    font-variant-numeric: tabular-nums;
  }
  .credits b {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 24px;
    font-weight: 400;
    line-height: 1;
    color: ${({ theme }) => theme.accent};
    white-space: nowrap;
  }
  .credits small {
    display: block;
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .group h3 {
    margin: 0 0 12px;
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkSoft};
  }
  .line {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    padding: 5px 0;
    font-size: 15px;
    color: ${({ theme }) => theme.inkSoft};
    font-variant-numeric: tabular-nums;
  }
  .line small {
    display: block;
    font-size: 13px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .line b {
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
    white-space: nowrap;
  }
  .sum {
    margin-top: 10px;
    padding-top: 14px;
    border-top: 1px solid ${({ theme }) => theme.line};
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    font-size: 15.5px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .sum strong {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 40px;
    font-weight: 400;
    letter-spacing: -0.02em;
    line-height: 1;
    color: ${({ theme }) => theme.ink};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .sum strong small {
    font-family: Inter, system-ui, sans-serif;
    font-size: 14px;
    letter-spacing: 0;
    color: ${({ theme }) => theme.inkSoft};
    margin-left: 6px;
  }
  .from {
    margin: 10px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkMuted};
  }

  @media (max-width: 450px) {
    padding: 22px 18px;
    .group.now {
      padding: 16px 14px 14px;
    }
  }
`;

const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.lineStrong};
  border-radius: 12px;
  overflow: hidden;

  button {
    width: 48px;
    height: 48px;
    border: 0;
    background: ${({ theme }) => theme.surface};
    font: inherit;
    font-size: 20px;
    color: ${({ theme }) => theme.ink};
    cursor: pointer;
  }
  button:hover {
    background: ${({ theme }) => theme.bg2};
  }
  button:disabled {
    color: ${({ theme }) => theme.inkFaint};
    cursor: default;
  }
  output {
    min-width: 56px;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.ink};
  }
`;

const Button = styled.button`
  height: 42px;
  padding: 0 20px;
  border-radius: 999px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ theme, $primary }) => ($primary ? theme.accent : theme.lineStrong)};
  background: ${({ theme, $primary }) => ($primary ? theme.accent : "transparent")};
  color: ${({ theme, $primary }) => ($primary ? theme.onFill : theme.ink)};

  &:hover {
    background: ${({ theme, $primary }) => ($primary ? theme.accentHover : theme.bg2)};
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 24px;
`;

/* Where invoices go, prefilled with the account email. */
const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 18px;
  max-width: 420px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.inkSoft};

  input {
    height: 42px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.lineStrong};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.ink};
    font: inherit;
    font-size: 14.5px;
  }
  input:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
    border-color: ${({ theme }) => theme.accent};
  }
  input[aria-invalid="true"] {
    border-color: ${({ theme }) => theme.danger};
  }
  small {
    font-weight: 400;
    color: ${({ theme }) => theme.inkMuted};
  }
`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Agreement to the terms, required before Pay is enabled. */
const Agree = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 22px;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.inkSoft};
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    /* The phone-wide 44px input min-height would stretch the checkbox off its text line. */
    min-height: 0;
    margin: 2px 0 0;
    flex-shrink: 0;
    accent-color: ${({ theme }) => theme.accent};
    cursor: pointer;
  }
  a {
    color: ${({ theme }) => theme.accent};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.p`
  margin: 14px 0 0;
  font-size: 13.5px;
  color: ${({ theme }) => theme.danger};
`;

/* Back from payment: the whole pane, hero on top filling the height, next steps below. */
const DonePage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: calc(100dvh - 5rem);
`;

const DoneHero = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px 56px 52px;
  border-radius: 28px;
  background:
    radial-gradient(900px 420px at 88% -20%, ${({ theme }) => theme.accentSoft}, transparent 65%),
    ${({ theme }) => theme.bg2};

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    width: fit-content;
    height: 32px;
    padding: 0 14px 0 10px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
    background: ${({ theme, $tone }) => ($tone === "ok" ? theme.successSoft : theme.surface)};
    color: ${({ theme, $tone }) => ($tone === "ok" ? theme.success : theme.inkSoft)};
    border: 1px solid ${({ theme, $tone }) => ($tone === "ok" ? "transparent" : theme.line)};
  }
  .eyebrow i {
    display: grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border-radius: 99px;
    background: ${({ theme }) => theme.success};
    color: ${({ theme }) => theme.onFill};
  }
  h1 {
    margin: 26px 0 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 84px;
    font-weight: 400;
    line-height: 0.98;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.ink};
    max-width: 14ch;
  }
  h1 em {
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
  .lede {
    margin: 22px 0 0;
    max-width: 58ch;
    font-size: 17px;
    line-height: 1.55;
    color: ${({ theme }) => theme.inkSoft};
  }

  @media (max-width: 1100px) {
    h1 {
      font-size: 64px;
    }
  }
  @media (max-width: 760px) {
    padding: 30px 22px 28px;
    border-radius: 20px;
    h1 {
      font-size: 46px;
    }
    .lede {
      font-size: 15.5px;
    }
  }
`;

const Stats = styled.div`
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  padding: 22px 24px 24px;
  border-radius: 18px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};

  .label {
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkMuted};
  }
  .value {
    margin-top: 10px;
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 40px;
    line-height: 1;
    letter-spacing: -0.015em;
    color: ${({ theme }) => theme.ink};
    min-height: 40px;
  }
  .value.pending {
    width: 60%;
    height: 34px;
    margin-top: 13px;
    border-radius: 8px;
    background: ${({ theme }) => theme.lineSoft};
    animation: ck-pulse 1.4s ease-in-out infinite;
  }
  .hint {
    margin-top: 8px;
    font-size: 13.5px;
    line-height: 1.45;
    color: ${({ theme }) => theme.inkSoft};
  }

  @media (max-width: 760px) {
    padding: 18px 18px 20px;
    .value {
      font-size: 32px;
    }
  }
`;

const DoneActions = styled.div`
  margin-top: 34px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const NextSteps = styled.section`
  padding: 36px 40px 40px;
  border-radius: 28px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};

  .head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px 24px;
  }
  h2 {
    margin: 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 36px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -0.015em;
    color: ${({ theme }) => theme.ink};
  }
  h2 em {
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
  .sub {
    margin: 0;
    font-size: 15px;
    color: ${({ theme }) => theme.inkMuted};
  }

  @media (max-width: 760px) {
    padding: 24px 20px 24px;
    border-radius: 20px;
    h2 {
      font-size: 30px;
    }
  }
`;

const StepGrid = styled.div`
  margin-top: 26px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 0;
  padding: 22px 22px 20px;
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg2};
  font: inherit;
  color: ${({ theme }) => theme.ink};
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;

  .icon {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${({ theme }) => theme.surface};
    border: 1px solid ${({ theme }) => theme.line};
    color: ${({ theme }) => theme.accent};
  }
  .n {
    margin-top: 18px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkMuted};
  }
  h3 {
    margin: 6px 0 0;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  p {
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkSoft};
  }
  .go {
    margin-top: auto;
    padding-top: 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.accent};
  }

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    transform: translateY(-2px);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

/* One add-on: copy and controls on the left, a small illustration of the result on the right. */
const AddOn = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 16px;
  overflow: hidden;

  & + & {
    margin-top: 14px;
  }

  .copy {
    padding: 22px 24px 22px;
  }
  .eyebrow {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.accent};
  }
  h2 {
    margin: 4px 0 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 26px;
    font-weight: 400;
    line-height: 1.05;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.ink};
  }
  .desc {
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkSoft};
    max-width: 44ch;
  }
  .controls {
    margin-top: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .price {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid ${({ theme }) => theme.line};
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px 12px;
    font-size: 14px;
    color: ${({ theme }) => theme.inkSoft};
    font-variant-numeric: tabular-nums;
  }
  .price strong {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 28px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.ink};
  }
  .fine {
    margin: 6px 0 0;
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .fine button {
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    color: ${({ theme }) => theme.accent};
    cursor: pointer;
  }
  .fine button:hover {
    text-decoration: underline;
  }
  .preview {
    margin: 8px;
    border-radius: 12px;
    background: ${({ theme }) => theme.bg2};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 18px 20px;
    min-width: 0;
  }
  .preview > * {
    width: 100%;
    max-width: 360px;
    box-sizing: border-box;
  }

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    .preview {
      margin: 0 8px 8px;
      padding: 16px;
    }
  }
  @media (max-width: 760px) {
    .copy {
      padding: 18px 18px 18px;
    }
  }
  /* Give the sample job rows room: at 375 the 24px inset left titles ~110px. */
  @media (max-width: 450px) {
    .preview {
      margin: 0 8px 8px;
      padding: 14px;
    }
  }
`;

/* First-month balance: plan credits plus whatever extra is chosen. */
const BalanceCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 16px 18px 18px;
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};

  .label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkSoft};
  }
  .figure {
    margin-top: 8px;
    display: flex;
    align-items: baseline;
    gap: 12px;
  }
  .figure strong {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 34px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.ink};
    font-variant-numeric: tabular-nums;
  }
  .figure span {
    font-size: 14px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .bar {
    margin-top: 14px;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    display: flex;
    background: ${({ theme }) => theme.bg2};
  }
  .bar i {
    display: block;
    height: 100%;
    transition: width 200ms ease;
  }
  .bar .plan {
    background: ${({ theme }) => theme.accent};
  }
  .bar .extra {
    background: ${({ theme }) => theme.accent2};
    opacity: 0.45;
  }
  .legend {
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
    font-variant-numeric: tabular-nums;
  }
  .legend i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 8px;
    vertical-align: 0;
  }
  .legend .plan {
    background: ${({ theme }) => theme.accent};
  }
  .legend .extra {
    background: ${({ theme }) => theme.accent2};
    opacity: 0.45;
  }

  @media (max-width: 450px) {
    padding: 14px 14px 16px;
    .figure {
      flex-wrap: wrap;
    }
    .figure strong {
      font-size: 30px;
    }
  }
`;

const PreviewNote = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.inkSoft};
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

/* What the Jobs tab looks like once a target list is watched. Illustrative rows only. */
const JobsPreview = styled.div`
  width: 100%;

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
    font-size: 11px;
  }
  .head b {
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkSoft};
  }
  .head span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .head span::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 99px;
    background: ${({ theme }) => theme.success};
  }
  .row {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    box-sizing: border-box;
    padding: 10px 12px;
    border-radius: 10px;
    background: ${({ theme }) => theme.surface};
    border: 1px solid ${({ theme }) => theme.line};
  }
  .row + .row {
    margin-top: 8px;
  }
  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 99px;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 600;
    background: ${({ theme }) => theme.accentSoft};
    color: ${({ theme }) => theme.accent};
  }
  .avatar.warm {
    background: ${({ theme }) => theme.secondarySoft};
    color: ${({ theme }) => theme.secondary};
  }
  .avatar.rose {
    background: ${({ theme }) => theme.dangerSoft || theme.secondarySoft};
    color: ${({ theme }) => theme.danger || theme.secondary};
  }
  .title {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 16px;
    line-height: 1.1;
    color: ${({ theme }) => theme.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sub {
    margin-top: 2px;
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkSoft};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .new {
    height: 22px;
    padding: 0 9px;
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    background: ${({ theme }) => theme.accentSoft};
    color: ${({ theme }) => theme.accent};
  }

  @media (max-width: 450px) {
    .row {
      grid-template-columns: 28px minmax(0, 1fr) auto;
      gap: 8px;
      padding: 9px 10px;
    }
    .avatar {
      width: 28px;
      height: 28px;
      font-size: 11px;
    }
    .title {
      font-size: 15px;
    }
    .sub {
      font-size: 12px;
    }
  }
`;

const SAMPLE_JOBS = [
  { initials: "BM", tone: "rose", title: "Senior Associate, Capital Markets", sub: "Baker McKenzie · Hong Kong" },
  { initials: "AS", tone: "warm", title: "Associate, Client Service", sub: "AlphaSights · Singapore" },
  { initials: "AO", tone: "", title: "Knowledge Lawyer, Banking", sub: "A&O Shearman · London" },
];

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 22px;

  @media (max-width: 450px) {
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 24px;
  }
`;

const BigButton = styled(Button)`
  height: 46px;
  padding: 0 22px;
  border-radius: 12px;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  gap: 12px;

  /* Both buttons side by side ran ~400px wide, past a 375 screen. */
  @media (max-width: 450px) {
    height: 48px;
    padding: 0 18px;
    font-size: 15px;
    gap: 8px;
  }
`;

/* Next renewal, for the "from" line under the total. */
const nextRenewal = (yearly) => {
  const d = new Date();
  if (yearly) d.setFullYear(d.getFullYear() + 1);
  else d.setMonth(d.getMonth() + 1);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const spin = {
  display: "inline-block",
  width: 14,
  height: 14,
  border: "2px solid currentColor",
  borderTopColor: "transparent",
  borderRadius: "50%",
  marginRight: 8,
  verticalAlign: -2,
  animation: "ck-spin .7s linear infinite",
};

const paidPlans = PLANS.filter((p) => p.monthly != null && !p.trial);
const planOf = (id) => paidPlans.find((p) => p.id === id) || paidPlans[1];

// The seats in the URL: "id:plan,new:plan,…" from the plans page (a bare "plan" means a new seat).
function parseSeats(raw, fallbackPlan) {
  const text = String(raw || "").trim();
  const list = text
    .split(",")
    .map((part) => {
      const [id, plan] = part.includes(":") ? part.split(":") : ["new", part];
      return paidPlans.some((p) => p.id === plan) ? { id: id && id !== "new" ? id : null, plan } : null;
    })
    .filter(Boolean)
    .slice(0, 50);
  return list.length ? list : [{ id: null, plan: planOf(fallbackPlan).id }];
}

// Seats grouped by tier, in catalogue order: [[plan, count], …].
const tiersOf = (seats) => paidPlans.map((p) => [p, seats.filter((s) => s.plan === p.id).length]).filter(([, n]) => n > 0);
const tierLabel = (seats) => tiersOf(seats).map(([p, n]) => `${n}× ${p.name}`).join(", ");

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const FIRST_STEPS = [
  {
    icon: FiBriefcase,
    title: "Track a company",
    text: "Add a firm you recruit for. New roles from its careers page land in Jobs each morning.",
    cta: "Go to Jobs",
    to: "/jobs",
  },
  {
    icon: FiUserPlus,
    title: "Run a candidate search",
    text: "Describe the role in a sentence or two and get a ranked shortlist of people.",
    cta: "Find candidates",
    to: "/find-candidates",
  },
  {
    icon: FiAtSign,
    title: "Look up a work email",
    text: "Pick anyone on a shortlist and get a verified business address for a flat 12 credits.",
    cta: "Open Outreach",
    to: "/outreach",
  },
  {
    icon: FiSend,
    title: "Draft the first message",
    text: "A first email written from the profile and the job, ready for you to edit and send.",
    cta: "Start drafting",
    to: "/outreach",
  },
];

const longDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : null;
};

// Back from Airwallex: poll until the payment has been applied.
function DoneView({ topup, forWho = "" }) {
  const navigate = useNavigate();
  const { checkSession } = useAuth();
  const { balance } = useCredits();
  const [state, setState] = React.useState("waiting"); // waiting | ready | slow
  const [status, setStatus] = React.useState(null);
  const tries = React.useRef(0);

  React.useEffect(() => {
    let alive = true;
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const res = await fetchWithAuth(`/api/billing/status?sync=${tries.current <= 3 || tries.current % 5 === 0 ? 1 : 0}`);
        const body = await res.json().catch(() => ({}));
        if (!alive) return;
        if (res.ok) {
          setStatus(body);
          if (topup || body.status === "ACTIVE") {
            setState("ready");
            checkSession?.();
            creditsChanged(); // the purchase just landed on the wallet
            return;
          }
        }
      } catch {
        if (!alive) return;
      }
      if (tries.current >= DONE_POLL_LIMIT) return setState("slow");
      timer = setTimeout(poll, DONE_POLL_MS);
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [topup, checkSession]);

  const ready = state === "ready";
  const planName = status?.planName;
  const renews = longDate(status?.renewsAt);
  const seats = status?.seats;

  const headline = () => {
    if (state === "waiting") return <>Confirming your <em>payment</em>.</>;
    if (state === "slow") return <>Still confirming your <em>payment</em>.</>;
    if (topup) return <>Credits <em>added</em>.</>;
    if (planName) return <>You're on <em>{planName}</em>.</>;
    return <>You're all <em>set</em>.</>;
  };
  const lede = () => {
    if (state === "waiting") return "This usually takes a few seconds. Keep this page open and your plan will appear here the moment it lands.";
    if (state === "slow") return "The payment is taking longer than usual to reach us. Your account will update on its own, and nothing else is needed from you.";
    if (topup) {
      return forWho
        ? `They're on ${forWho}'s balance now, ready to spend on searches, lookups and drafts. Your plan and renewal date are unchanged.`
        : "They're on your balance now, ready to spend on searches, lookups and drafts. Your plan and renewal date are unchanged.";
    }
    return "Your plan is live. Credits refresh on your renewal date each month, and anything extra you added today is already in your balance.";
  };

  const stat = (label, value, hint) => (
    <Stat>
      <div className="label">{label}</div>
      {value == null ? <div className="value pending" /> : <div className="value">{value}</div>}
      {hint && <div className="hint">{hint}</div>}
    </Stat>
  );

  return (
    <DonePage>
      <DoneHero $tone={ready ? "ok" : "wait"}>
        <span className="eyebrow">
          {ready ? (
            <>
              <i>
                <FiCheck size={12} strokeWidth={3} />
              </i>
              Payment confirmed
            </>
          ) : (
            <>
              <span style={{ ...spin, marginRight: 0 }} />
              {state === "slow" ? "Taking longer than usual" : "Talking to your bank"}
            </>
          )}
        </span>
        <h1>{headline()}</h1>
        <p className="lede">{lede()}</p>

        <Stats>
          {stat("Credits/month", ready && status?.creditsPerMonth ? formatCredits(status.creditsPerMonth) : ready ? "—" : null, seats > 1 ? `Across ${seats} seats` : "Per seat, every month")}
          {stat("In your balance now", typeof balance === "number" && ready ? formatCredits(balance) : null, "Ready to spend today")}
          {stat("Plan", ready ? planName || "—" : null, ready && status?.cycle ? (status.cycle === "yearly" ? "Billed yearly" : "Billed monthly") : "Change it any time in Settings")}
          {stat("Renews on", ready ? renews || "—" : null, "Credits top back up on this date")}
        </Stats>

        <DoneActions>
          <Button type="button" $primary disabled={!ready} onClick={() => (window.location.href = "/")}>
            Open scoutm8
            <FiArrowRight size={16} style={{ marginLeft: 8, verticalAlign: -3 }} />
          </Button>
          <Button type="button" disabled={!ready} onClick={() => navigate("/settings?tab=billing")}>
            View invoice
          </Button>
        </DoneActions>
      </DoneHero>

      <NextSteps>
        <div className="head">
          <h2>
            Make the first one <em>count</em>.
          </h2>
          <p className="sub">Four things most recruiters do in their first hour.</p>
        </div>
        <StepGrid>
          {FIRST_STEPS.map(({ icon: Icon, title, text, cta, to }, i) => (
            <Step key={title} type="button" onClick={() => navigate(to)}>
              <span className="icon">
                <Icon size={18} />
              </span>
              <span className="n">Step {i + 1}</span>
              <h3>{title}</h3>
              <p>{text}</p>
              <span className="go">
                {cta}
                <FiArrowRight size={14} />
              </span>
            </Step>
          ))}
        </StepGrid>
      </NextSteps>
    </DonePage>
  );
}

/* Changing a live plan: no payment page. Upgrades charge the prorated difference to the saved
   card now; downgrades and cycle switches wait for the renewal date. */
const ChangeCard = styled(Card)`
  .lines {
    margin: 22px 0 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 14px;
  }
  .lines li {
    display: grid;
    grid-template-columns: 150px 1fr;
    gap: 18px;
    font-size: 15px;
    line-height: 1.45;
  }
  .lines li span:first-child {
    color: ${({ theme }) => theme.inkSoft};
  }
  .lines li b {
    font-weight: 600;
  }
  .lines small {
    display: block;
    margin-top: 2px;
    font-size: 13.5px;
    color: ${({ theme }) => theme.inkSoft};
  }

  /* A 150px label column leaves the values ~130px on a phone; stack label over value. */
  @media (max-width: 450px) {
    padding: 20px 18px 22px;
    .lines li {
      grid-template-columns: 1fr;
      gap: 2px;
    }
  }
`;

const RowNote = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--ink-muted);
`;

function ChangeView({ seatSpec, cycle, backTo = "/plans", backLabel = "Back to plans", focusSeatId = "", focusName = "" }) {
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  // Existing seats go by id (their people stay in them); new ones carry just a plan.
  const seatsBody = React.useMemo(() => seatSpec.map((s) => (s.id ? { id: s.id, plan: s.plan } : { plan: s.plan })), [seatSpec]);
  const bodyKey = JSON.stringify({ cycle, seats: seatsBody });

  React.useEffect(() => {
    let alive = true;
    setPreview(null);
    setError("");
    fetchWithAuth("/api/billing/change/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: bodyKey,
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || "We couldn't work out that change just now.");
        if (alive) setPreview(body);
      })
      .catch((err) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, [bodyKey]);

  const confirm = async () => {
    if (pending) return;
    setPending(true);
    setError("");
    try {
      const res = await fetchWithAuth("/api/billing/change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyKey,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't change the plan just now.");
      setResult(body);
      creditsChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setPending(false);
    }
  };

  const immediate = preview?.kind === "immediate";
  const fmtPlan = (p) => `${p.label}, billed ${p.cycle}, ${p.seats} seat${p.seats === 1 ? "" : "s"}`;
  // One colleague's seat: the card talks about that seat, and about billing only if it changed for everyone.
  const seatBefore = focusSeatId ? preview?.current?.seatList?.find((x) => x.id === focusSeatId) : null;
  const seatAfter = focusSeatId ? preview?.target?.seatList?.find((x) => x.id === focusSeatId) : null;
  const seatFocus = Boolean(seatBefore && seatAfter);
  const cycleChanged = preview && preview.current?.cycle !== preview.target?.cycle;
  const per = cycle === "yearly" ? "/year" : "/month";
  const targetLabel = preview?.target?.label || tierLabel(seatSpec);
  const seatCredits = tiersOf(seatSpec).map(([p]) => `${formatCredits(p.credits)} credits/month per ${p.name} seat`).join(" · ");

  if (result) {
    const scheduled = result.kind === "scheduled";
    const when = longDate(result.status?.pendingChange?.effectiveAt || result.preview?.effectiveAt);
    return (
      <DonePage>
        <DoneHero $tone="ok">
          <span className="eyebrow">
            <i>
              <FiCheck size={12} strokeWidth={3} />
            </i>
            {scheduled ? "Change scheduled" : result.kind === "kept" ? "Plan kept" : "Plan updated"}
          </span>
          <h1>
            {scheduled ? (
              <>
                <em>{seatFocus ? `${focusName || "That seat"} on ${seatAfter.planName}` : targetLabel}</em> from {when}.
              </>
            ) : result.kind === "kept" ? (
              <>
                Your plan <em>carries on</em>.
              </>
            ) : seatFocus ? (
              <>
                {focusName || "That seat"} is on <em>{seatAfter.planName}</em>.
              </>
            ) : (
              <>
                You're on <em>{targetLabel}</em>.
              </>
            )}
          </h1>
          <p className="lede">
            {scheduled
              ? `You won't be charged until ${when || "then"}; your current seats run until that date. You can undo this from Settings any time before it.`
              : result.kind === "kept"
                ? "The scheduled change was dropped and your current plan continues as before."
                : "The difference for the rest of this period has been charged to your saved card, and the extra credits for those days are already in the right balances."}
          </p>
          <DoneActions>
            <Button type="button" $primary onClick={() => (window.location.href = "/")}>
              Open scoutm8
              <FiArrowRight size={16} style={{ marginLeft: 8, verticalAlign: -3 }} />
            </Button>
            <Button type="button" onClick={() => navigate("/settings?tab=team")}>
              Manage seats in Settings
            </Button>
          </DoneActions>
        </DoneHero>
      </DonePage>
    );
  }

  return (
    <>
      <Crumb type="button" onClick={() => navigate(backTo)}>
        ← {backLabel}
      </Crumb>
      <Head>
        <h1>
          Review your <em>change.</em>
        </h1>
        <p>{immediate ? "Upgrades and added seats start right away on your saved card." : "Moving a seat down, dropping a seat or switching how you're billed starts on your renewal date."}</p>
      </Head>
      <Layout>
        <div>
          <ChangeCard>
            <h2>What changes</h2>
            {!preview && !error && <p>Working out the cost…</p>}
            {preview && (
              <ul className="lines">
                {seatFocus ? (
                  <>
                    <li>
                      <span>Now</span>
                      <b>
                        {focusName || "This seat"} on {seatBefore.planName}
                        <small>{formatCredits(seatBefore.credits)} credits/month</small>
                      </b>
                    </li>
                    <li>
                      <span>Changing to</span>
                      <b>
                        {seatBefore.plan === seatAfter.plan ? `${seatAfter.planName}, unchanged` : seatAfter.planName}
                        {seatBefore.plan !== seatAfter.plan && <small>{formatCredits(seatAfter.credits)} credits/month for {focusName || "this seat"}</small>}
                      </b>
                    </li>
                    {cycleChanged && (
                      <li>
                        <span>Billing</span>
                        <b>
                          {preview.current.cycle} → {preview.target.cycle}
                          <small>This applies to every seat on the plan, not just {focusName || "this one"}.</small>
                        </b>
                      </li>
                    )}
                  </>
                ) : (
                  <>
                    <li>
                      <span>Now</span>
                      <b>{fmtPlan(preview.current)}</b>
                    </li>
                    <li>
                      <span>Changing to</span>
                      <b>
                        {fmtPlan(preview.target)}
                        <small>
                          {seatCredits}
                          {preview.added > 0 ? `. ${preview.added} new seat${preview.added === 1 ? "" : "s"} to fill from Settings → Team` : ""}
                          {preview.removed > 0 ? `. ${preview.removed} seat${preview.removed === 1 ? "" : "s"} dropped` : ""}
                        </small>
                      </b>
                    </li>
                  </>
                )}
                <li>
                  <span>Today</span>
                  {immediate ? (
                    <b>
                      {usd(preview.today)}, charged to your saved card now
                      <small>
                        The difference for the {preview.daysLeft != null ? `${preview.daysLeft} days` : "days"} left in this period. The full new price starts on your renewal date.
                      </small>
                    </b>
                  ) : (
                    <b>
                      Nothing
                      <small>You won't be charged until {longDate(preview.effectiveAt) || "your renewal date"}. Your current seats run until then.</small>
                    </b>
                  )}
                </li>
                {immediate && preview.creditsNow > 0 && (
                  <li>
                    <span>Credits now</span>
                    <b>
                      +{formatCredits(preview.creditsNow)}
                      <small>
                        The extra credits for the {preview.daysLeft != null ? `${preview.daysLeft} days` : "days"} left in this period, into the upgraded seats' balances. The full amount comes on renewal.
                      </small>
                    </b>
                  </li>
                )}
                <li>
                  <span>From {longDate(preview.renewsAt) || "renewal"}</span>
                  <b>
                    {usd(preview.nextCharge?.amount ?? 0)}{per}
                    {preview.nextCharge?.maintenanceMonthly > 0 && <small>Plus {usd(preview.nextCharge.maintenanceMonthly)}/month for tracked companies.</small>}
                  </b>
                </li>
              </ul>
            )}
            {error && <ErrorText>{error}</ErrorText>}
            <Actions>
              <Button type="button" onClick={() => navigate(backTo)} disabled={pending}>
                Back
              </Button>
              <Button type="button" $primary onClick={confirm} disabled={pending || !preview}>
                {pending && <span style={spin} />}
                {pending ? "Applying" : immediate ? "Confirm and pay" : "Confirm change"}
              </Button>
            </Actions>
          </ChangeCard>
        </div>
        <Summary>
          <div className="head">
            <h2>{seatFocus ? "Whole plan after" : "Your seats"}</h2>
          </div>
          <p className="summary">
            <b>{targetLabel}</b>, {cycle}, {seatSpec.length} seat{seatSpec.length === 1 ? "" : "s"}.
          </p>
          <div className="group now">
            <h3>Today</h3>
            <div className="line">
              <span>
                {immediate ? "Upgrade difference" : "No charge"}
                {immediate && <small>Rest of this period, {preview?.daysLeft ?? "…"} days</small>}
              </span>
              <b>{usd(preview?.today ?? 0)}</b>
            </div>
            <div className="sum">
              <span>Due today</span>
              <strong>{usd(preview?.today ?? 0)}</strong>
            </div>
            {preview && !immediate && <RowNote>You won't be charged until {longDate(preview.effectiveAt) || "your renewal date"}.</RowNote>}
          </div>
          <div className="group later">
            <h3>From {longDate(preview?.kind === "scheduled" ? preview?.effectiveAt : preview?.renewsAt) || "your renewal date"}</h3>
            {tiersOf(seatSpec).map(([p, count]) => (
              <div className="line" key={p.id}>
                <span>
                  {p.name} plan
                  <small>
                    {count} seat{count === 1 ? "" : "s"} × {usd(cycle === "yearly" ? annualTotal(p.monthly) : p.monthly)}
                  </small>
                </span>
                <b>{usd((cycle === "yearly" ? annualTotal(p.monthly) : p.monthly) * count)}</b>
              </div>
            ))}
            {preview?.nextCharge?.trackedFirms > 0 && (
              <div className="line">
                <span>
                  Tracked companies
                  <small>
                    {preview.nextCharge.trackedFirms} × {usd(ADDON_MONTHLY_USD)}/month{cycle === "yearly" ? ", billed monthly" : ""}
                  </small>
                </span>
                <b>{usd(preview.nextCharge.maintenance)}</b>
              </div>
            )}
            <div className="sum">
              <span>Then</span>
              <strong>
                {usd(preview?.nextCharge?.amount ?? tiersOf(seatSpec).reduce((n, [p, count]) => n + (cycle === "yearly" ? annualTotal(p.monthly) : p.monthly) * count, 0))}
                <small>{per}</small>
              </strong>
            </div>
            {cycle === "yearly" && preview?.nextCharge?.maintenance > 0 && (
              <RowNote>Plus {usd(preview.nextCharge.maintenance)}/month for tracked companies, charged monthly.</RowNote>
            )}
          </div>
        </Summary>
      </Layout>
    </>
  );
}

export default function CheckoutPage() {
  const q = useQuery();
  const navigate = useNavigate();
  const { user, features: appFeatures } = useAuth();
  const teamOn = Boolean(appFeatures?.teamSeats); // colleague and seat links only mean something with team seats on
  const { balance: walletBalance } = useCredits();
  const done = q.get("done") === "1";
  const topupOnly = q.get("topup") === "1";
  const paying = q.get("pay") === "1";
  // The admin buying credits for a colleague's seat: their wallet, not the payer's.
  const forUserId = (teamOn && q.get("for")) || "";
  const forName = (teamOn && q.get("forName")) || "";
  const forWho = forUserId ? forName || "your colleague" : "";

  // The order comes from the plans page in the URL, so refresh and back both keep it.
  const yearly = q.get("cycle") === "yearly";
  const seatSpec = React.useMemo(() => parseSeats(q.get("seats"), q.get("plan")), [q]);
  const plan = planOf(seatSpec[0]?.plan); // the admin's own seat
  const seats = seatSpec.length;
  const seatPlans = seatSpec.map((s) => s.plan);
  const tiers = tiersOf(seatSpec);
  const [blocks, setBlocks] = React.useState(() => {
    const fromUrl = Number(q.get("blocks"));
    if (fromUrl > 0) return Math.min(50, fromUrl);
    return topupOnly ? 1 : 0;
  });
  // Target list tracking is arranged after payment; the count rides along in the URL.
  const [firms, setFirms] = React.useState(() => Math.max(0, Math.min(500, Number(q.get("firms")) || 0)));
  const [pending, setPending] = React.useState(false);
  // Back from the Airwallex page restores this page from the bfcache with the redirect still pending; re-enable Pay.
  React.useEffect(() => {
    const reset = () => setPending(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);
  const [error, setError] = React.useState("");
  const [demoOpen, setDemoOpen] = React.useState(false);
  // Invoice email: the account's current login email, from the session straight away and
  // confirmed by the server; the user can change it for this order.
  const [billingEmail, setBillingEmail] = React.useState(() => user?.email || "");
  const [emailLoaded, setEmailLoaded] = React.useState(() => Boolean(user?.email));
  const [touched, setTouched] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    fetchWithAuth("/api/billing/contact")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        // A saved billing email wins over the login email the page prefilled with.
        if (alive && body?.email) setBillingEmail((cur) => (!cur.trim() || cur.trim() === (body.loginEmail || "") ? body.email : cur));
      })
      .catch(() => {})
      .finally(() => alive && setEmailLoaded(true));
    return () => {
      alive = false;
    };
  }, []);
  React.useEffect(() => {
    if (user?.email) setBillingEmail((cur) => cur.trim() || user.email);
  }, [user?.email]);
  const emailOk = EMAIL_RE.test(billingEmail.trim());

  if (done) {
    return (
      <Page>
        <style>{`@keyframes ck-spin{to{transform:rotate(360deg)}}@keyframes ck-pulse{0%,100%{opacity:.55}50%{opacity:1}}`}</style>
        <DoneView topup={topupOnly} forWho={forWho} />
      </Page>
    );
  }
  if (q.get("change") === "1") {
    return (
      <Page>
        <style>{`@keyframes ck-spin{to{transform:rotate(360deg)}}`}</style>
        <ChangeView
          seatSpec={seatSpec}
          cycle={yearly ? "yearly" : "monthly"}
          backTo={teamOn && q.get("back") === "team" ? "/settings?tab=team" : "/plans"}
          backLabel={teamOn && q.get("back") === "team" ? "Back to Team" : "Back to plans"}
          focusSeatId={(teamOn && q.get("seat")) || ""}
          focusName={forName}
        />
      </Page>
    );
  }

  const seatPrice = (p) => (yearly ? annualTotal(p.monthly) : p.monthly);
  const planTotal = tiers.reduce((n, [p, count]) => n + seatPrice(p) * count, 0);
  const planMonthly = tiers.reduce((n, [p, count]) => n + p.monthly * count, 0);
  const topupTotal = blocks * TOPUP_BLOCK_USD;
  // Top-up preview: what the wallet holds now, plus what is being bought.
  const walletNow = Math.max(0, typeof walletBalance === "number" ? walletBalance : 0);
  const topupAfter = walletNow + blocks * TOPUP_BLOCK_CREDITS;
  // Custom tracked companies: setup per firm once, then a monthly amount per firm.
  const trackingMonthly = firms * ADDON_MONTHLY_USD;
  const trackingSetup = firms * ADDON_SETUP_USD;
  // Everything on the one payment page today.
  const dueToday = topupOnly ? topupTotal : planTotal + trackingSetup + trackingMonthly + topupTotal;
  const base = `/checkout?plan=${plan.id}&cycle=${yearly ? "yearly" : "monthly"}&seats=${encodeURIComponent(seatSpec.map((s) => `${s.id || "new"}:${s.plan}`).join(","))}`;
  const withExtras = `${base}&blocks=${blocks}&firms=${firms}`;
  // Allowances are per seat: this account gets one seat's credits, plus any extras it buys.
  const balance = plan.credits + blocks * TOPUP_BLOCK_CREDITS;

  const pay = async () => {
    if (pending) return;
    if (!topupOnly && !emailOk) {
      setTouched(true);
      setError("Please enter a valid email for invoices.");
      return;
    }
    if (!agreed) {
      setError("Please agree to the terms and privacy policy to continue.");
      return;
    }
    setPending(true);
    setError("");
    try {
      let res;
      if (topupOnly) {
        res = await fetchWithAuth("/api/billing/topup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ blocks, agreed, forUserId: forUserId || undefined }),
        });
      } else {
        // One hosted checkout for the plan, extra credits and tracked companies together.
        res = await fetchWithAuth("/api/billing/checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan: plan.id, cycle: yearly ? "yearly" : "monthly", seatPlans, blocks, firms, billingEmail: billingEmail.trim(), agreed }),
        });
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) throw new Error(body?.error || "We couldn't start checkout. Please try again.");
      // Only ever hand the browser to Airwallex's own pages.
      if (!isAirwallexUrl(body.url)) throw new Error("We couldn't start checkout. Please try again.");
      window.location.href = body.url;
    } catch (err) {
      setError(errorText(err, "We couldn't start checkout. Please try again."));
      setPending(false);
    }
  };

  const title = topupOnly ? (
    <>
      Extra <em>credits.</em>
    </>
  ) : paying ? (
    <>
      Review and <em>pay.</em>
    </>
  ) : (
    <>
      Add <em>ons.</em>
    </>
  );
  const sub = topupOnly
    ? forWho
      ? `A one-off top-up for ${forWho}, added to their balance as soon as it clears. Charged to you.`
      : "A one-off top-up, added to your balance as soon as it clears."
    : paying
      ? "Payment is handled on a secure page. Cards, Apple Pay and Google Pay are accepted."
      : "Optional, and you can add these from Settings later.";

  return (
    <Page>
      <style>{`@keyframes ck-spin{to{transform:rotate(360deg)}}`}</style>
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      {(topupOnly || paying) && (
        <Crumb type="button" onClick={() => (topupOnly ? navigate(forUserId ? "/settings?tab=team" : "/settings?tab=credits") : navigate(withExtras))}>
          ← {topupOnly ? (forUserId ? "Back to Team" : "Back to Credits & Usage") : "Back to add-ons"}
        </Crumb>
      )}
      <Head>
        <h1>{title}</h1>
        <p>{sub}</p>
      </Head>

      <Layout>
        <div>
          {topupOnly && (
            <>
              <AddOn aria-labelledby="topup-title">
                <div className="copy">
                  <span className="eyebrow">One-off top-up</span>
                  <h2 id="topup-title">Extra credits</h2>
                  <p className="desc">Sold in blocks of {formatCredits(TOPUP_BLOCK_CREDITS)}. They never expire and are spent the same way as your plan's credits.</p>
                  <div className="controls">
                    <Stepper>
                      <button type="button" onClick={() => setBlocks((n) => Math.max(1, n - 1))} disabled={blocks <= 1} aria-label="Fewer credits">
                        −
                      </button>
                      <output>{formatCredits(blocks * TOPUP_BLOCK_CREDITS)}</output>
                      <button type="button" onClick={() => setBlocks((n) => Math.min(50, n + 1))} disabled={blocks >= 50} aria-label="More credits">
                        +
                      </button>
                    </Stepper>
                    credits
                  </div>
                  <div className="price">
                    <strong>{usd(topupTotal)}</strong>
                    <span>/ {formatCredits(blocks * TOPUP_BLOCK_CREDITS)} credits</span>
                  </div>
                </div>
                <div className="preview">
                  <div>
                    {forWho ? (
                      <BalanceCard>
                        <div className="label">Goes to {forWho}'s balance</div>
                        <div className="figure">
                          <strong>+{formatCredits(blocks * TOPUP_BLOCK_CREDITS)}</strong>
                          <span>credits</span>
                        </div>
                        <div className="legend">
                          <span>
                            <i className="extra" />
                            They never expire
                          </span>
                        </div>
                      </BalanceCard>
                    ) : (
                    <BalanceCard>
                      <div className="label">Your balance after</div>
                      <div className="figure">
                        <strong>{formatCredits(topupAfter)}</strong>
                        <span>credits</span>
                      </div>
                      <div className="bar" aria-hidden="true">
                        <i className="plan" style={{ width: `${topupAfter > 0 ? (walletNow / topupAfter) * 100 : 0}%` }} />
                        <i className="extra" style={{ width: `${topupAfter > 0 ? ((blocks * TOPUP_BLOCK_CREDITS) / topupAfter) * 100 : 0}%` }} />
                      </div>
                      <div className="legend">
                        <span>
                          <i className="plan" />
                          Now · {formatCredits(walletNow)}
                        </span>
                        <span>
                          <i className="extra" />
                          Adding · {formatCredits(blocks * TOPUP_BLOCK_CREDITS)}
                        </span>
                      </div>
                    </BalanceCard>
                    )}
                    <PreviewNote>
                      ≈ {formatCredits(Math.floor((blocks * TOPUP_BLOCK_CREDITS) / 500))}–{formatCredits(Math.floor((blocks * TOPUP_BLOCK_CREDITS) / 100))} more
                      candidate searches of 20 people
                    </PreviewNote>
                  </div>
                </div>
              </AddOn>
              <Card style={{ marginTop: 16 }}>
                <Agree style={{ marginTop: 0 }}>
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                  <span>
                    By paying I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noreferrer">
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a href="/privacy-policy" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </span>
                </Agree>
                <Actions>
                  <Button type="button" onClick={() => navigate("/settings?tab=credits")} disabled={pending}>
                    Back
                  </Button>
                  <Button type="button" $primary onClick={pay} disabled={pending || !agreed}>
                    {pending && <span style={spin} />}
                    {pending ? "Opening secure payment" : `Pay ${usd(topupTotal)}`}
                  </Button>
                </Actions>
                {error && <ErrorText>{error}</ErrorText>}
              </Card>
            </>
          )}
          {!topupOnly && !paying && (
            <>
              <AddOn aria-labelledby="addon-credits">
                <div className="copy">
                  <span className="eyebrow">One-off top-up</span>
                  <h2 id="addon-credits">Extra credits</h2>
                  <p className="desc">Handy for a busy first month. Spent the same way as your plan's credits.</p>
                  <div className="controls">
                    <Stepper>
                      <button type="button" onClick={() => setBlocks((n) => Math.max(0, n - 1))} disabled={blocks <= 0} aria-label="Fewer credits">
                        −
                      </button>
                      <output>{formatCredits(blocks * TOPUP_BLOCK_CREDITS)}</output>
                      <button type="button" onClick={() => setBlocks((n) => Math.min(50, n + 1))} disabled={blocks >= 50} aria-label="More credits">
                        +
                      </button>
                    </Stepper>
                    credits
                  </div>
                  <div className="price">
                    <strong>{usd(topupTotal)}</strong>
                    <span>/ {formatCredits(blocks * TOPUP_BLOCK_CREDITS)} credits</span>
                  </div>
                </div>
                <div className="preview">
                  <div>
                    <BalanceCard>
                      <div className="label">Your first month balance</div>
                      <div className="figure">
                        <strong>{formatCredits(balance)}</strong>
                        <span>credits</span>
                      </div>
                      <div className="bar" aria-hidden="true">
                        <i className="plan" style={{ width: `${(plan.credits / balance) * 100}%` }} />
                        <i className="extra" style={{ width: `${((blocks * TOPUP_BLOCK_CREDITS) / balance) * 100}%` }} />
                      </div>
                      <div className="legend">
                        <span>
                          <i className="plan" />
                          {plan.name} · {formatCredits(plan.credits)} per seat
                        </span>
                        <span>
                          <i className="extra" />
                          Extra · {formatCredits(blocks * TOPUP_BLOCK_CREDITS)}
                        </span>
                      </div>
                    </BalanceCard>
                    <PreviewNote>
                      ≈ {formatCredits(Math.floor(balance / 500))}–{formatCredits(Math.floor(balance / 100))} candidate searches of 20 people
                    </PreviewNote>
                  </div>
                </div>
              </AddOn>

              <AddOn aria-labelledby="addon-tracking">
                <div className="copy">
                  <span className="eyebrow">A list of</span>
                  <h2 id="addon-tracking">Custom tracked companies</h2>
                  <p className="desc">
                    Tell us how many firms you want to pull job postings from. After payment we'll be in touch for the list, and every new
                    opening lands in your Jobs tab daily.
                  </p>
                  <div className="controls">
                    <Stepper>
                      <button type="button" onClick={() => setFirms((n) => Math.max(0, n - 1))} disabled={firms <= 0} aria-label="Fewer firms">
                        −
                      </button>
                      <output>{firms}</output>
                      <button type="button" onClick={() => setFirms((n) => Math.min(500, n + 1))} disabled={firms >= 500} aria-label="More firms">
                        +
                      </button>
                    </Stepper>
                    firms
                  </div>
                  <div className="price">
                    <strong>{usd(trackingMonthly)}</strong>
                    <span>/month, plus {usd(trackingSetup)} setup</span>
                  </div>
                  <p className="fine">
                    {usd(ADDON_MONTHLY_USD)}/firm/month, {usd(ADDON_SETUP_USD)} setup per firm · Prefer a walkthrough?{" "}
                    <button type="button" onClick={() => setDemoOpen(true)}>
                      Book a demo
                    </button>
                  </p>
                </div>
                <div className="preview">
                  <JobsPreview aria-hidden="true">
                    <div className="head">
                      <b>Jobs · New today</b>
                      <span>Watching your list</span>
                    </div>
                    {SAMPLE_JOBS.map((j) => (
                      <div className="row" key={j.title}>
                        <div className={`avatar ${j.tone}`}>{j.initials}</div>
                        <div>
                          <div className="title">{j.title}</div>
                          <div className="sub">{j.sub}</div>
                        </div>
                        <span className="new">New</span>
                      </div>
                    ))}
                  </JobsPreview>
                </div>
              </AddOn>

              <Footer>
                <BigButton type="button" onClick={() => navigate("/plans")}>
                  <FiArrowLeft size={18} /> Plans
                </BigButton>
                <BigButton type="button" $primary onClick={() => navigate(`${withExtras}&pay=1`)}>
                  {blocks > 0 || firms > 0 ? "Next: payment" : "Skip, go to payment"} <FiArrowRight size={18} />
                </BigButton>
              </Footer>
            </>
          )}

          {!topupOnly && paying && (
            <Card>
              <h2>Review and pay</h2>
              <p>Card and billing details are taken on a secure Airwallex page next. Cards, Apple Pay and Google Pay are accepted.</p>
              <Field>
                Send invoices to
                <input
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  disabled={!emailLoaded}
                  autoComplete="email"
                  aria-invalid={touched && !emailOk ? "true" : undefined}
                />
                <small>Receipts and monthly invoices go here. Your login email stays the same.</small>
              </Field>
              {firms > 0 && <p style={{ marginTop: 16 }}>We'll be in touch for your list of companies as soon as the plan is active.</p>}
              <Agree>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                <span>
                  By paying I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy-policy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  .
                </span>
              </Agree>
              <Actions>
                <Button type="button" onClick={() => navigate(withExtras)} disabled={pending}>
                  Back
                </Button>
                <Button type="button" $primary onClick={pay} disabled={pending || !agreed}>
                  {pending && <span style={spin} />}
                  {pending ? "Opening secure payment" : "Pay now"}
                </Button>
              </Actions>
              {error && <ErrorText>{error}</ErrorText>}
            </Card>
          )}
        </div>

        <Summary>
          <div className="head">
            <h2>Your order</h2>
            {!topupOnly && !paying && (
              <button type="button" className="change" onClick={() => navigate("/plans")}>
                Change plan
              </button>
            )}
          </div>
          {topupOnly ? (
            <div className="group now">
              <h3>Today</h3>
              <div className="line">
                <span>
                  Extra credits
                  <small>
                    {formatCredits(blocks * TOPUP_BLOCK_CREDITS)} credits, {usd(TOPUP_BLOCK_USD)} / {formatCredits(TOPUP_BLOCK_CREDITS)} credits
                  </small>
                </span>
                <b>{usd(topupTotal)}</b>
              </div>
              <div className="sum">
                <span>Due today</span>
                <strong>{usd(topupTotal)}</strong>
              </div>
            </div>
          ) : (
            <>
              <p className="summary">
                <b>{tierLabel(seatSpec)}</b>, {yearly ? "yearly" : "monthly"}, {seats} seat{seats === 1 ? "" : "s"}
                {firms > 0 ? `, ${firms} tracked compan${firms === 1 ? "y" : "ies"}` : ""}
                {blocks > 0 ? `, ${formatCredits(blocks * TOPUP_BLOCK_CREDITS)} extra credits` : ""}.
              </p>

              <div className="group now">
                <h3>{yearly ? "Today" : "This month"}</h3>
                {tiers.map(([p, count]) => (
                  <div className="line" key={p.id}>
                    <span>
                      {p.name} plan
                      <small>
                        {count} seat{count === 1 ? "" : "s"} × {usd(seatPrice(p))}
                        {yearly ? ", for the year" : ""}
                      </small>
                    </span>
                    <b>{usd(seatPrice(p) * count)}</b>
                  </div>
                ))}
                {blocks > 0 && (
                  <div className="line">
                    <span>
                      Extra credits
                      <small>
                        {formatCredits(blocks * TOPUP_BLOCK_CREDITS)} credits, {usd(TOPUP_BLOCK_USD)} / {formatCredits(TOPUP_BLOCK_CREDITS)} credits, one-off
                      </small>
                    </span>
                    <b>{usd(topupTotal)}</b>
                  </div>
                )}
                {firms > 0 && (
                  <>
                    <div className="line">
                      <span>
                        Company setup
                        <small>
                          {firms} × {usd(ADDON_SETUP_USD)}, one-off
                        </small>
                      </span>
                      <b>{usd(trackingSetup)}</b>
                    </div>
                    <div className="line">
                      <span>
                        Company maintenance
                        <small>
                          {firms} × {usd(ADDON_MONTHLY_USD)}
                        </small>
                      </span>
                      <b>{usd(trackingMonthly)}</b>
                    </div>
                  </>
                )}
                <div className="sum">
                  <span>Due today</span>
                  <strong>{usd(dueToday)}</strong>
                </div>
                <div className="credits">
                  <span>
                    Credits this month
                    <small>
                      your {plan.name} seat{seats > 1 ? "; every seat gets its own plan's credits" : ""}
                      {blocks > 0 ? `, plus ${formatCredits(blocks * TOPUP_BLOCK_CREDITS)} extra on your account` : ""}
                    </small>
                  </span>
                  <b>{formatCredits(plan.credits)}</b>
                </div>
              </div>

              <div className="group later">
                <h3>Every month after</h3>
                {tiers.map(([p, count]) => (
                  <div className="line" key={p.id}>
                    <span>
                      {p.name} plan
                      <small>
                        {yearly
                          ? `paid yearly, ${usd(annualTotal(p.monthly) * count)} on ${nextRenewal(true)}`
                          : `${count} seat${count === 1 ? "" : "s"} × ${usd(p.monthly)}`}
                      </small>
                    </span>
                    <b>{yearly ? "—" : usd(p.monthly * count)}</b>
                  </div>
                ))}
                {firms > 0 && (
                  <div className="line">
                    <span>
                      Company maintenance
                      <small>
                        {firms} × {usd(ADDON_MONTHLY_USD)}
                      </small>
                    </span>
                    <b>{usd(trackingMonthly)}</b>
                  </div>
                )}
                <div className="sum">
                  <span>From {nextRenewal(false)}</span>
                  <strong>
                    {usd(yearly ? trackingMonthly : planMonthly + trackingMonthly)}
                    <small>/month</small>
                  </strong>
                </div>
                <div className="credits">
                  <span>
                    Credits every month
                    <small>your {plan.name} seat{seats > 1 ? "; every seat gets its own plan's credits" : ""}</small>
                  </span>
                  <b>{formatCredits(plan.credits)}</b>
                </div>
              </div>
            </>
          )}
        </Summary>
      </Layout>
    </Page>
  );
}
